import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRefreshEffect } from "@/context/RefreshContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox, Users, AlertTriangle } from "lucide-react";

const empty = () => ({ name: "", warnung: "", ansprechpartner: "", deals: "" });

export default function GangView({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(empty());
  const [members, setMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gangregister");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshEffect(load);

  const fetchMembers = async (name) => {
    if (!name || !name.trim()) {
      setMembers([]);
      return;
    }
    try {
      const { data } = await api.get("/gangregister/members", { params: { name: name.trim() } });
      setMembers(data);
    } catch (e) {
      setMembers([]);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setValues(empty());
    setMembers([]);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setValues({ name: row.name, warnung: row.warnung || "", ansprechpartner: row.ansprechpartner || "", deals: row.deals || "" });
    setMembers(row.mitglieder || []);
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { name: values.name, warnung: values.warnung, ansprechpartner: values.ansprechpartner, deals: values.deals };
      if (editing) {
        await api.put(`/gangregister/${editing.id}`, payload);
        toast.success("Gang aktualisiert");
      } else {
        await api.post("/gangregister", payload);
        toast.success("Gang erstellt");
      }
      setDialogOpen(false);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/gangregister/${confirm.id}`);
      toast.success("Gang gelöscht");
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const filtered = rows.filter((r) =>
    !search.trim() ? true : [r.name, r.warnung, r.ansprechpartner, r.deals].some((v) => String(v ?? "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input data-testid="gang-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Gang suchen…" className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
        </div>
        <Button data-testid="gang-create-btn" onClick={openCreate} className="ml-auto rounded-sm bg-primary hover:bg-blue-600 text-white gap-2">
          <Plus className="h-4 w-4" /> Gang hinzufügen
        </Button>
      </div>

      <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {["Name", "Anzahl Mitglieder", "Warnung / Achtung", "Ansprechpartner", "Deals"].map((c) => (
                <TableHead key={c} className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 h-11">{c}</TableHead>
              ))}
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 w-[110px]">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-zinc-800 hover:bg-transparent"><TableCell colSpan={6} className="text-center py-10 text-zinc-500 font-mono text-sm">Wird geladen…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow className="border-zinc-800 hover:bg-transparent"><TableCell colSpan={6} className="text-center py-14"><div className="flex flex-col items-center gap-2 text-zinc-600"><Inbox className="h-8 w-8" /><span className="font-mono text-sm">Keine Gangs erfasst</span></div></TableCell></TableRow>
            ) : (
              filtered.map((r) => (
                <TableRow key={r.id} data-testid="gang-row" className="border-zinc-800 hover:bg-zinc-900/50 transition-colors duration-150">
                  <TableCell className="text-sm font-medium text-zinc-100 align-top">{r.name}</TableCell>
                  <TableCell className="align-top">
                    <Badge className="rounded-sm border border-primary/30 bg-primary/10 text-primary font-mono text-[11px] gap-1.5">
                      <Users className="h-3.5 w-3.5" /> {r.mitglieder_count}
                    </Badge>
                  </TableCell>
                  <TableCell className="align-top">
                    {r.warnung ? (
                      <span className="inline-flex items-start gap-2 max-w-md text-sm text-amber-300"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span className="whitespace-pre-wrap">{r.warnung}</span></span>
                    ) : <span className="text-zinc-600">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-300 align-top">{r.ansprechpartner || <span className="text-zinc-600">—</span>}</TableCell>
                  <TableCell className="text-sm text-zinc-300 align-top"><span className="block max-w-xs whitespace-pre-wrap">{r.deals || <span className="text-zinc-600">—</span>}</span></TableCell>
                  <TableCell className="text-right align-top">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid="gang-edit" size="icon" variant="ghost" onClick={() => openEdit(r)} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-primary hover:bg-primary/10" title="Bearbeiten"><Pencil className="h-4 w-4" /></Button>
                      <Button data-testid="gang-delete" size="icon" variant="ghost" onClick={() => setConfirm(r)} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10" title="Löschen"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-zinc-100">{editing ? "Gang bearbeiten" : "Gang hinzufügen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Name <span className="text-red-500">*</span></Label>
              <Input data-testid="gang-name" value={values.name} onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))} onBlur={(e) => fetchMembers(e.target.value)} required className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Warnung / Achtung</Label>
              <Textarea data-testid="gang-warnung" value={values.warnung} onChange={(e) => setValues((v) => ({ ...v, warnung: e.target.value }))} rows={3} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary resize-none" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                Ansprechpartner {members.length > 0 && <span className="text-zinc-600 normal-case tracking-normal">({members.length} Mitglieder)</span>}
              </Label>
              <Select value={values.ansprechpartner || ""} onValueChange={(v) => setValues((s) => ({ ...s, ansprechpartner: v }))}>
                <SelectTrigger data-testid="gang-ansprechpartner" className="bg-transparent border-zinc-700 rounded-sm focus:ring-primary">
                  <SelectValue placeholder={members.length ? "Mitglied auswählen…" : "Keine Mitglieder – erst Personen der Gang zuordnen"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
                  {members.map((m) => (
                    <SelectItem key={m} value={m} className="rounded-sm text-sm">{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Deals</Label>
              <Textarea data-testid="gang-deals" value={values.deals} onChange={(e) => setValues((v) => ({ ...v, deals: e.target.value }))} rows={3} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary resize-none" />
            </div>
            <DialogFooter className="pt-2 gap-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-sm text-zinc-400">Abbrechen</Button>
              <Button type="submit" disabled={submitting} data-testid="gang-submit" className="rounded-sm bg-primary hover:bg-blue-600 text-white">{submitting ? "Speichern…" : "Speichern"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">Gang löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Dieser Vorgang kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="gang-confirm" onClick={doDelete} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">Bestätigen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
